import {
  addNewConnection,
  cancelConnection,
  getAcceptedConnections,
  getAllConnections,
  getConnection,
} from "@/app/api/connection";
import { AddNewPatientDTO, ConnectionDTO } from "@/app/api/connection/types";
import { ErrorResponse } from "@/app/types/error";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

interface ConnectionState {
  connectionList: ConnectionDTO[];
  acceptedConnectionList: ConnectionDTO[];
  selectedConnection: ConnectionDTO;
  selectedConnectionId: string;
  addConnection: AddNewPatientDTO;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
}

const initialState: ConnectionState = {
  connectionList: [],
  acceptedConnectionList: [],
  selectedConnection: {} as ConnectionDTO,
  selectedConnectionId: "",
  addConnection: {} as AddNewPatientDTO,
  status: "idle",
  error: null,
};

export const createConnection = createAsyncThunk(
  "connection/add",
  async (data: AddNewPatientDTO, { rejectWithValue }) => {
    try {
      const response = await addNewConnection(data);

      if (
        response &&
        typeof response === "object" &&
        "status" in response &&
        response.status === "fail"
      ) {
        return rejectWithValue((response as ErrorResponse).message);
      }

      return response as { data: ConnectionDTO };
    } catch (error) {
      if (error instanceof Error && error.message) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("An unknown error occurred");
    }
  },
);

export const getConnectionById = createAsyncThunk(
  "connection/get",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await getConnection(id);

      if (
        response &&
        typeof response === "object" &&
        "status" in response &&
        response.status === "fail"
      ) {
        return rejectWithValue((response as ErrorResponse).message);
      }

      return response as { data: ConnectionDTO };
    } catch (error) {
      if (error instanceof Error && error.message) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("An unknown error occurred");
    }
  },
);

export const getConnections = createAsyncThunk(
  "connection/getAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAllConnections();

      if (
        response &&
        typeof response === "object" &&
        "status" in response &&
        response.status === "fail"
      ) {
        return rejectWithValue((response as ErrorResponse).message);
      }

      return response as { data: ConnectionDTO[] };
    } catch (error) {
      if (error instanceof Error && error.message) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("An unknown error occurred");
    }
  },
);

export const getAcceptedConnectionsService = createAsyncThunk(
  "connection/accepted",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAcceptedConnections();

      if (
        response &&
        typeof response === "object" &&
        "status" in response &&
        response.status === "fail"
      ) {
        return rejectWithValue((response as ErrorResponse).message);
      }

      return response as { data: ConnectionDTO[] };
    } catch (error) {
      if (error instanceof Error && error.message) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("An unknown error occurred");
    }
  },
);

export const cancelConnectionById = createAsyncThunk(
  "connection/cancel",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await cancelConnection(id);

      if (
        response &&
        typeof response === "object" &&
        "status" in response &&
        response.status === "fail"
      ) {
        return rejectWithValue((response as ErrorResponse).message);
      }

      return response;
    } catch (error) {
      if (error instanceof Error && error.message) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("An unknown error occurred");
    }
  },
);

const connectionSlice = createSlice({
  name: "connection",
  initialState,
  reducers: {
    updateAddConnectionState: (state, action) => {
      state.addConnection = {
        ...state.addConnection,
        ...action.payload,
      };
    },
    setSelectedConnectionId: (state, action) => {
      state.selectedConnectionId = action.payload;
    },
    resetStatus: (state) => {
      state.status = "idle";
    },
    resetSelectedConnectionId: (state) => {
      state.selectedConnectionId = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createConnection.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(createConnection.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = null;
        state.connectionList.push(action.payload.data);
      })
      .addCase(createConnection.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || null;
      })
      .addCase(getConnectionById.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getConnectionById.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.selectedConnection = action.payload.data;
      })
      .addCase(getConnectionById.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || null;
      })
      .addCase(getConnections.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getConnections.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = null;
        state.connectionList = action.payload.data;
      })
      .addCase(getConnections.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || null;
      })
      .addCase(cancelConnectionById.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(cancelConnectionById.fulfilled, (state) => {
        state.status = "succeeded";
        state.error = null;
      })
      .addCase(cancelConnectionById.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || null;
      })
      .addCase(getAcceptedConnectionsService.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getAcceptedConnectionsService.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.acceptedConnectionList = action.payload.data;
        state.error = null;
      })
      .addCase(getAcceptedConnectionsService.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || null;
      });
  },
});

export const {
  updateAddConnectionState,
  setSelectedConnectionId,
  resetStatus,
  resetSelectedConnectionId,
} = connectionSlice.actions;

export const connectionReducer = connectionSlice.reducer;
